/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  Box,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import { CustomFilterV2, DialogDefaultV2, TableV2 } from '../../../components/v2';
import * as actionsEquipamentos from '../../../store/equipamento/actions';
import * as Roles from '../../../constants/role';
import * as selectorsAuth from '../../../store/auth/reducer';
import Formulario from './Formulario';
import { buildEquipamentoFilter } from './helpers';

const QUERY_EQUIPAMENTO_ID = 'equipamentoId';

const getEquipamentoIdFromSearch = (search) => {
  const params = new URLSearchParams(search || '');
  const raw = params.get(QUERY_EQUIPAMENTO_ID) || params.get('id');
  return raw && String(raw).trim() ? String(raw).trim() : null;
};

const EquipamentosV2 = ({
  deleteEquipamento,
  authData,
  exportEquipamento,
  history,
  location,
}) => {
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [openExport, setOpenExport] = useState(false);
  const [localFilter, setLocalFilter] = useState(() => {
    const params = new URLSearchParams(location?.search || '');
    const tipoEquipamento = params.get('tipoEquipamento');
    const posseEletropontes = params.get('posseEletropontes');
    return {
      ...(tipoEquipamento ? { tipoEquipamento } : {}),
      ...(posseEletropontes === 'true' || posseEletropontes === 'false'
        ? { posseEletropontes }
        : {}),
    };
  });
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);

  const isAdmin = () =>
    authData && (authData.role === Roles.ADMIN || authData.role === Roles.SUPER_ADMIN);

  useEffect(() => {
    const deepLinkId = getEquipamentoIdFromSearch(location?.search);
    if (!deepLinkId) return;
    if (deepLinkId === 'novo') {
      setId(null);
      setOpen(true);
      return;
    }
    setId(deepLinkId);
    setOpen(true);
  }, [location?.search]);

  const replaceSearch = (mutate) => {
    const params = new URLSearchParams(location.search);
    mutate(params);
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
    });
  };

  const setEquipamentoIdInUrl = (equipamentoId) => {
    replaceSearch((params) => {
      params.delete('id');
      if (equipamentoId == null || equipamentoId === '') {
        params.delete(QUERY_EQUIPAMENTO_ID);
        return;
      }
      params.set(QUERY_EQUIPAMENTO_ID, String(equipamentoId));
    });
  };

  const openEquipamento = (rowId) => {
    setOpen(true);
    setId(rowId);
    setEquipamentoIdInUrl(rowId);
  };

  const onAdd = () => {
    setOpen(true);
    setId(null);
    setEquipamentoIdInUrl('novo');
  };

  const onClose = (isSave) => {
    if (isSave) {
      setReload((current) => !current);
    }
    setOpen(false);
    setId(null);
    setEquipamentoIdInUrl(null);
  };

  const headerActions = [];
  if (isAdmin()) {
    headerActions.push({
      label: 'Novo',
      onClick: onAdd,
      startIcon: <AddIcon />,
      variant: 'contained',
    });
    headerActions.push({
      label: 'Exportar',
      onClick: () => setOpenExport(true),
      startIcon: <FileDownloadOutlinedIcon />,
      variant: 'outlined',
    });
  }

  const columns = [
    { field: 'tipoEquipamentoNome', headerName: 'Tipo de Equipamento', flex: 0.8 },
    { field: 'numPatrimonio', headerName: 'Num. Patrimônio', flex: 0.5 },
    { field: 'modelo', headerName: 'Modelo', flex: 0.6 },
    { field: 'numSerie', headerName: 'Num. Série', flex: 0.5 },
    { field: 'codigoSisant', headerName: 'Código SISANT', flex: 0.5 },
    { field: 'codigoAnatel', headerName: 'Código ANATEL', flex: 0.5 },
    { field: 'ultimoRepasse', headerName: 'Último Repasse/Colaborador', flex: 0.8 },
    { field: 'categoriaRepasse', headerName: 'Categoria Repasse', flex: 0.5 },
  ];

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="h4">Equipamentos</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <TableV2
          useApi
          fetchUrl="equipamentos/filtro"
          filterData={localFilter}
          refreshKey={reload}
          columns={columns}
          rowHeight={52}
          onEdit={isAdmin() ? openEquipamento : undefined}
          onView={openEquipamento}
          onDelete={isAdmin() ? (rowId) => {
            deleteEquipamento(rowId);
            setReload((current) => !current);
          } : undefined}
          headerActions={headerActions}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          appliedFilter={localFilter}
          fields={[
            { field: 'useCodigo', codigoLabel: 'Modelo', md: 4, xs: 12 },
            { field: 'useNumPatrimonio', md: 4, xs: 12 },
            { field: 'useTipoEquipamento', md: 4, xs: 12 },
            { field: 'useNumSerie', md: 4, xs: 12 },
            { field: 'useColaborador', md: 4, xs: 12 },
            { field: 'usePosseEletropontes', md: 4, xs: 12 },
            { field: 'useSisant', md: 4, xs: 12 },
            { field: 'useAtivo', md: 4, xs: 12 },
          ]}
          onFilter={(filterData) => {
            const filter = buildEquipamentoFilter(filterData);
            setLocalFilter(filter);
            setOpenFilter(false);
          }}
        />

        <Formulario id={id} open={open} onClose={onClose} />

        <DialogDefaultV2
          open={openExport}
          handlerCancel={() => setOpenExport(false)}
          handlerConfirm={() => {
            exportEquipamento(localFilter, () => {
              setOpenExport(false);
            });
          }}
          title="Confirmação"
          confirmMessage="Sim"
          cancelMessage="Não"
        >
          <DialogContentText>
            Deseja baixar todas as informações filtradas?
          </DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

EquipamentosV2.propTypes = {
  deleteEquipamento: PropTypes.func.isRequired,
  exportEquipamento: PropTypes.func.isRequired,
  authData: PropTypes.object,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...actionsEquipamentos,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(EquipamentosV2));
